<template>
  <BaseModal
    :is-open="isOpen"
    :hidden="isHiddenDuringBoardHighlight"
    title="Round Complete"
    max-width="900px"
    :closable="currentPhase === 'summary'"
    :class="{ 'has-video-backdrop': showVideoBackdrop }"
    @close="handleClose"
  >
    <div class="round-end-container">
      <!-- Video Celebration Backdrop (plays during announcement) -->
      <Transition name="video-fade">
        <video
          v-if="showVideoBackdrop && celebrationVideo"
          ref="videoRef"
          class="round-end-video"
          :src="videoSource"
          autoplay
          muted
          playsinline
          @ended="onVideoEnded"
          @error="onVideoError"
        />
      </Transition>

      <!-- Continue Button (visible when waiting for user to advance) -->
      <Transition name="fade">
        <div v-if="waitingForContinue" class="continue-prompt">
          <button class="btn-continue-debug" @click="handleDebugContinue">
            <span class="material-symbols-outlined">arrow_forward</span>
            <span>Continue</span>
          </button>
        </div>
      </Transition>

      <!-- Phase 1: Went Out Announcement -->
      <Transition name="fade">
        <div v-if="currentPhase === 'announcement'" class="went-out-announcement">
          <div class="celebration-icon">
            <span class="material-symbols-outlined">emoji_events</span>
          </div>
          <h2 class="went-out-title">{{ wentOutPlayer }} Went Out!</h2>
          <div class="team-badge" :class="`team-${wentOutTeam.toLowerCase()}`">
            <span>{{ wentOutTeamName }}</span>
          </div>
        </div>
      </Transition>

      <!-- Phase 2-4: Winner Team Scoring -->
      <Transition name="fade">
        <div v-if="currentPhase === 'winner-canastas'" class="team-scoring">
          <h3 class="phase-title">{{ winnerTeamName }} - Canastas</h3>
          <CanastaDisplay
            ref="winnerCanastaRef"
            :team="winnerTeam"
            :team-name="winnerTeamName"
            :canastas="winnerScore.canastas"
            :is-animating="true"
            @count-complete="onWinnerCanastaComplete"
          />
        </div>
      </Transition>

      <Transition name="fade">
        <div v-if="currentPhase === 'winner-piles'" class="team-scoring">
          <h3 class="phase-title">{{ winnerTeamName }} - Card Points</h3>
          <CardPileCounter
            ref="winnerPileCounterRef"
            :team="winnerTeam"
            :team-name="winnerTeamName"
            :card-points="winnerScore.card_points"
            :canasta-base="winnerCanastaTotal"
            :is-animating="true"
            :show-combined="true"
            @deal-complete="onWinnerDealComplete"
            @count-complete="onWinnerPileComplete"
          />
        </div>
      </Transition>

      <Transition name="fade">
        <div v-if="currentPhase === 'winner-penalties'" class="team-scoring">
          <h3 class="phase-title">Penalties from {{ winnerTeamName }} hand and foot</h3>
          <PenaltyDisplay
            :team="winnerTeam"
            :red3-count="winnerRed3Count"
            :hand-penalty="winnerScore.penalties.hand_cards"
            :hand-card-count="winnerHandCardCount"
            :natural-canastas-canceled="winnerNaturalCanastasCanceled"
            :base-points="winnerBaseTotal"
            :is-animating="true"
            :show-math="true"
            :player-hands="winnerPlayerHands"
            :team-name="winnerTeamName"
            :opponent-penalty="loserScore.penalties.hand_cards"
            :going-out-bonus="winnerScore.going_out_bonus"
            @penalty-complete="onWinnerPenaltyComplete"
          />
        </div>
      </Transition>

      <!-- Team Pause Indicator -->
      <Transition name="fade">
        <div v-if="currentPhase === 'team-pause'" class="team-pause">
          <div class="pause-indicator">
            <span class="material-symbols-outlined">groups</span>
            <span>Now counting {{ loserTeamName }}...</span>
          </div>
        </div>
      </Transition>

      <!-- Phase 5-7: Loser Team Scoring -->
      <Transition name="fade">
        <div v-if="currentPhase === 'loser-canastas'" class="team-scoring">
          <h3 class="phase-title">{{ loserTeamName }} - Canastas</h3>
          <CanastaDisplay
            ref="loserCanastaRef"
            :team="loserTeam"
            :team-name="loserTeamName"
            :canastas="loserScore.canastas"
            :is-animating="true"
            @count-complete="onLoserCanastaComplete"
          />
        </div>
      </Transition>

      <Transition name="fade">
        <div v-if="currentPhase === 'loser-piles'" class="team-scoring">
          <h3 class="phase-title">{{ loserTeamName }} - Card Points</h3>
          <CardPileCounter
            ref="loserPileCounterRef"
            :team="loserTeam"
            :team-name="loserTeamName"
            :card-points="loserScore.card_points"
            :canasta-base="loserCanastaTotal"
            :is-animating="true"
            :show-combined="true"
            @deal-complete="onLoserDealComplete"
            @count-complete="onLoserPileComplete"
          />
        </div>
      </Transition>

      <Transition name="fade">
        <div v-if="currentPhase === 'loser-penalties'" class="team-scoring">
          <h3 class="phase-title">Penalties from {{ loserTeamName }} hand and foot</h3>
          <PenaltyDisplay
            :team="loserTeam"
            :red3-count="loserRed3Count"
            :hand-penalty="loserScore.penalties.hand_cards"
            :hand-card-count="loserHandCardCount"
            :natural-canastas-canceled="loserNaturalCanastasCanceled"
            :base-points="loserBaseTotal"
            :is-animating="true"
            :show-math="true"
            :player-hands="loserPlayerHands"
            :team-name="loserTeamName"
            :opponent-penalty="winnerScore.penalties.hand_cards"
            :going-out-bonus="loserScore.going_out_bonus"
            @penalty-complete="onLoserPenaltyComplete"
          />
        </div>
      </Transition>

      <!-- Phase 8: Round Summary -->
      <Transition name="fade">
        <div v-if="currentPhase === 'summary'" class="round-summary">
          <h3 class="summary-title">Round {{ roundNumber }} Complete</h3>

          <div class="teams-comparison">
            <!-- Team A -->
            <div
              class="team-card"
              :class="[
                `team-${winnerTeam.toLowerCase() === 'a' ? 'a' : 'b'}`,
                { 'is-winner': winnerTeam === 'A' },
              ]"
            >
              <div v-if="winnerTeam === 'A'" class="winner-crown">
                <span class="material-symbols-outlined">emoji_events</span>
              </div>
              <div class="team-header">
                <span class="team-name">{{ teamAName }}</span>
              </div>
              <div class="round-points-display">
                <span class="label">This Round</span>
                <span class="value">+{{ teamAScore.round_total.toLocaleString() }}</span>
              </div>
              <div class="total-score-display">
                <span class="value">{{ (data?.new_total_a || 0).toLocaleString() }}</span>
                <span class="label">Total Score</span>
              </div>
            </div>

            <!-- VS Divider -->
            <div class="vs-divider">VS</div>

            <!-- Team B -->
            <div
              class="team-card"
              :class="[
                `team-${winnerTeam.toLowerCase() === 'b' ? 'b' : 'a'}`,
                { 'is-winner': winnerTeam === 'B' },
              ]"
            >
              <div v-if="winnerTeam === 'B'" class="winner-crown">
                <span class="material-symbols-outlined">emoji_events</span>
              </div>
              <div class="team-header">
                <span class="team-name">{{ teamBName }}</span>
              </div>
              <div class="round-points-display">
                <span class="label">This Round</span>
                <span class="value">+{{ teamBScore.round_total.toLocaleString() }}</span>
              </div>
              <div class="total-score-display">
                <span class="value">{{ (data?.new_total_b || 0).toLocaleString() }}</span>
                <span class="label">Total Score</span>
              </div>
            </div>
          </div>

          <!-- Score Breakdown Accordion -->
          <div class="breakdown-accordion">
            <button
              class="breakdown-toggle"
              @click="showBreakdown = !showBreakdown"
            >
              <span class="material-symbols-outlined toggle-icon" :class="{ rotated: showBreakdown }">
                expand_more
              </span>
              <span>{{ showBreakdown ? 'Hide' : 'Show' }} Score Breakdown</span>
            </button>

            <Transition name="slide">
              <div v-if="showBreakdown" class="breakdown-content">
                <!-- Header row with team names -->
                <div class="breakdown-header">
                  <div class="breakdown-label"></div>
                  <div class="breakdown-value team-a-header">{{ teamAName }}</div>
                  <div class="breakdown-value team-b-header">{{ teamBName }}</div>
                </div>

                <!-- Breakdown rows -->
                <div
                  v-for="(row, index) in breakdownRows"
                  :key="index"
                  class="breakdown-row"
                  :class="{
                    'is-separator': row.isSeparator,
                    'is-total': row.isTotal,
                    'is-negative': row.a.startsWith('-') || row.b.startsWith('-')
                  }"
                >
                  <div class="breakdown-label">{{ row.label }}</div>
                  <div class="breakdown-value" :class="{ positive: row.a.startsWith('+'), negative: row.a.startsWith('-') }">
                    {{ row.a }}
                  </div>
                  <div class="breakdown-value" :class="{ positive: row.b.startsWith('+'), negative: row.b.startsWith('-') }">
                    {{ row.b }}
                  </div>
                </div>
              </div>
            </Transition>
          </div>

          <button class="btn-continue" @click="handleContinue">
            <span class="material-symbols-outlined">
              {{ isGameEnd ? 'emoji_events' : 'arrow_forward' }}
            </span>
            <span>{{ isGameEnd ? 'View Final Results' : 'Next Round' }}</span>
          </button>
        </div>
      </Transition>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import BaseModal from './BaseModal.vue'
import CanastaDisplay from '../scoring/CanastaDisplay.vue'
import CardPileCounter from '../scoring/CardPileCounter.vue'
import PenaltyDisplay from '../scoring/PenaltyDisplay.vue'
import { useRoundEndAnimations, NARRATION } from '../../composables/useRoundEndAnimations'
import { useSounds } from '../../composables/useSounds'
import { Logger, LogCategory } from '../../utils/Logger.class'
import { useVictoryVideo, type CelebrationVideo } from '../../composables/useVictoryVideo'

/**
 * RoundEndModal Component
 *
 * Orchestrates the complete round-end scoring sequence:
 * 1. "Went Out" announcement (2s)
 * 2. Winner team canastas (clean left, dirty+wild right) with voice counting
 * 3. Winner team card piles (100pt piles with scatter) with voice counting
 * 4. Winner team penalties (red 3s, hand cards)
 * 5. Team pause (2.5s)
 * 6. Loser team canastas
 * 7. Loser team card piles
 * 8. Loser team penalties
 * 9. Round summary with continue button
 */

type Phase =
  | 'idle'
  | 'announcement'
  | 'board-canastas-winner'  // NEW: Canastas highlighted on GameBoard
  | 'winner-canastas'        // DEPRECATED: Kept for fallback
  | 'winner-piles'
  | 'winner-penalties'
  | 'team-pause'
  | 'board-canastas-loser'   // NEW: Canastas highlighted on GameBoard
  | 'loser-canastas'         // DEPRECATED: Kept for fallback
  | 'loser-piles'
  | 'loser-penalties'
  | 'summary'

interface TeamScore {
  canastas: {
    natural: number
    mixed: number
    wild: number
  }
  card_points: number
  going_out_bonus: number
  penalties: {
    red_3s: number
    hand_cards: number
    natural_canastas_canceled?: number
  }
  round_total: number
}

// Full score breakdown from backend
interface ScoreBreakdown {
  meld_points?: number
  natural_canasta_bonus?: number
  mixed_canasta_bonus?: number
  wild_canasta_bonus?: number
  hidden_wild_bonus?: number
  going_out_bonus?: number
  hand_penalties?: number
  red_three_penalties?: number
  black_three_penalties?: number
  natural_canastas_canceled?: number
  natural_canasta_count?: number
  mixed_canasta_count?: number
  wild_canasta_count?: number
  total?: number
}

interface Props {
  isOpen: boolean
  data: {
    went_out?: { player_name: string; team: 'A' | 'B' }
    team_a_score?: TeamScore
    team_b_score?: TeamScore
    team_a_breakdown?: ScoreBreakdown
    team_b_breakdown?: ScoreBreakdown
    previous_total_a?: number
    previous_total_b?: number
    new_total_a?: number
    new_total_b?: number
    is_game_end?: boolean
    team_a_name?: string
    team_b_name?: string
    round_number?: number
    player_hands?: Array<{
      id: string
      username: string
      display_name?: string
      team: 'A' | 'B'
      seat: number
      is_bot: boolean
      hand: Array<{ rank: string; suit: string }>
      foot: Array<{ rank: string; suit: string }>
    }>
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  continue: []
  close: []
  'request-board-highlight': [team: 'A' | 'B', canastas: { natural: number; mixed: number; wild: number }, teamName: string]
}>()

const { delay, teamPause, formatPointsForSpeech, voiceOnlyPhase, estimateVoiceDuration } = useRoundEndAnimations()
const { speak, speakCounting, playGoOut, playFinalTotal, playClick, playCanastaComplete } = useSounds()

// Video celebration composable
const victoryVideo = useVictoryVideo()
const celebrationVideo = ref<CelebrationVideo | null>(null)
const videoRef = ref<HTMLVideoElement | null>(null)
const showVideoBackdrop = ref(false)
const videoError = ref(false)

// Computed: Video source URL
const videoSource = computed(() => {
  if (!celebrationVideo.value) return ''
  return victoryVideo.getVideoSource(celebrationVideo.value)
})

// Video event handlers
function onVideoEnded(): void {
  // Video finished, fade out backdrop
  showVideoBackdrop.value = false
}

function onVideoError(): void {
  console.warn('[RoundEndModal] Video failed to load')
  videoError.value = true
  showVideoBackdrop.value = false
}

// Select and start video for round end celebration
function startVideoBackdrop(): void {
  if (victoryVideo.hasVideosAvailable.value && !videoError.value) {
    celebrationVideo.value = victoryVideo.selectRandom()
    if (celebrationVideo.value) {
      showVideoBackdrop.value = true
    }
  }
}

// Refs for CardPileCounter components (for voice orchestration)
const winnerPileCounterRef = ref<InstanceType<typeof CardPileCounter> | null>(null)
const loserPileCounterRef = ref<InstanceType<typeof CardPileCounter> | null>(null)

// Refs for CanastaDisplay components (for blip animation)
const winnerCanastaRef = ref<InstanceType<typeof CanastaDisplay> | null>(null)
const loserCanastaRef = ref<InstanceType<typeof CanastaDisplay> | null>(null)

// Animation state
const currentPhase = ref<Phase>('idle')
const skipped = ref(false)

// Score breakdown accordion state
const showBreakdown = ref(false)

// Debug pause state - waits for user to click Continue after each phase
const waitingForContinue = ref(false)
let continueResolver: (() => void) | null = null

/**
 * Wait for user to click Continue button (debug pause point)
 */
function waitForUserContinue(): Promise<void> {
  return new Promise((resolve) => {
    waitingForContinue.value = true
    continueResolver = resolve
  })
}

/**
 * Handle Continue button click
 */
function handleDebugContinue(): void {
  waitingForContinue.value = false
  if (continueResolver) {
    continueResolver()
    continueResolver = null
  }
}

// Canasta totals (accumulated during animation)
const winnerCanastaTotal = ref(0)
const loserCanastaTotal = ref(0)

// Base totals (canastas + card points, before penalties)
const winnerBaseTotal = computed(() => winnerCanastaTotal.value + winnerScore.value.card_points)
const loserBaseTotal = computed(() => loserCanastaTotal.value + loserScore.value.card_points)

/**
 * Computed properties for team identification
 */
const wentOutTeam = computed(() => props.data?.went_out?.team || 'A')
const wentOutPlayer = computed(() => props.data?.went_out?.player_name || 'Player')

// Winner is the team that went out
const winnerTeam = computed(() => wentOutTeam.value)
const loserTeam = computed<'A' | 'B'>(() => (wentOutTeam.value === 'A' ? 'B' : 'A'))

// Team names
const teamAName = computed(() => props.data?.team_a_name || 'Team A')
const teamBName = computed(() => props.data?.team_b_name || 'Team B')
const winnerTeamName = computed(() => (winnerTeam.value === 'A' ? teamAName.value : teamBName.value))
const loserTeamName = computed(() => (loserTeam.value === 'A' ? teamAName.value : teamBName.value))
const wentOutTeamName = computed(() => winnerTeamName.value)

// Team scores
const teamAScore = computed<TeamScore>(() => props.data?.team_a_score || defaultScore())
const teamBScore = computed<TeamScore>(() => props.data?.team_b_score || defaultScore())
const winnerScore = computed(() => (winnerTeam.value === 'A' ? teamAScore.value : teamBScore.value))
const loserScore = computed(() => (loserTeam.value === 'A' ? teamAScore.value : teamBScore.value))

// Red 3 counts (calculated from penalties / 500)
const winnerRed3Count = computed(() => Math.floor(winnerScore.value.penalties.red_3s / 500))
const loserRed3Count = computed(() => Math.floor(loserScore.value.penalties.red_3s / 500))

// Hand card counts (estimated from penalty value)
const winnerHandCardCount = computed(() => Math.ceil(winnerScore.value.penalties.hand_cards / 10))
const loserHandCardCount = computed(() => Math.ceil(loserScore.value.penalties.hand_cards / 10))

// Player hands filtered by team for card fan display
const winnerPlayerHands = computed(() => {
  const hands = props.data?.player_hands
  if (!Array.isArray(hands)) return []
  return hands.filter(p => p.team === winnerTeam.value)
})
const loserPlayerHands = computed(() => {
  const hands = props.data?.player_hands
  if (!Array.isArray(hands)) return []
  return hands.filter(p => p.team === loserTeam.value)
})

// New totals
const winnerNewTotal = computed(() =>
  winnerTeam.value === 'A'
    ? (props.data?.new_total_a || 0)
    : (props.data?.new_total_b || 0)
)
const loserNewTotal = computed(() =>
  loserTeam.value === 'A'
    ? (props.data?.new_total_a || 0)
    : (props.data?.new_total_b || 0)
)

// Other props
const roundNumber = computed(() => props.data?.round_number || 1)
const isGameEnd = computed(() => props.data?.is_game_end || false)

/**
 * Whether modal should be hidden during board canasta highlighting
 * Modal hides so player can see canastas on GameBoard being highlighted
 */
const isHiddenDuringBoardHighlight = computed(() =>
  ['board-canastas-winner', 'board-canastas-loser'].includes(currentPhase.value)
)

/**
 * Score breakdown data for accordion display
 */
const teamABreakdown = computed<ScoreBreakdown>(() => props.data?.team_a_breakdown || {})
const teamBBreakdown = computed<ScoreBreakdown>(() => props.data?.team_b_breakdown || {})

/**
 * Format breakdown row with label and values for both teams
 */
function formatBreakdownRow(label: string, teamAValue: number | undefined, teamBValue: number | undefined): { label: string; a: string; b: string } | null {
  const a = teamAValue ?? 0
  const b = teamBValue ?? 0
  // Skip row if both values are 0
  if (a === 0 && b === 0) return null
  return {
    label,
    a: a >= 0 ? `+${a.toLocaleString()}` : a.toLocaleString(),
    b: b >= 0 ? `+${b.toLocaleString()}` : b.toLocaleString(),
  }
}

/**
 * Compute breakdown rows for display
 */
const breakdownRows = computed(() => {
  const a = teamABreakdown.value
  const b = teamBBreakdown.value
  const rows: Array<{ label: string; a: string; b: string; isTotal?: boolean; isSeparator?: boolean }> = []

  // Positive points section
  const meldRow = formatBreakdownRow('Meld Points', a.meld_points, b.meld_points)
  if (meldRow) rows.push(meldRow)

  const naturalRow = formatBreakdownRow(`Natural Canastas (${a.natural_canasta_count || 0}/${b.natural_canasta_count || 0})`, a.natural_canasta_bonus, b.natural_canasta_bonus)
  if (naturalRow) rows.push({ label: 'Natural Canastas', a: naturalRow.a, b: naturalRow.b })

  const mixedRow = formatBreakdownRow('Mixed Canastas', a.mixed_canasta_bonus, b.mixed_canasta_bonus)
  if (mixedRow) rows.push(mixedRow)

  const wildRow = formatBreakdownRow('Wild Canastas', a.wild_canasta_bonus, b.wild_canasta_bonus)
  if (wildRow) rows.push(wildRow)

  const hiddenRow = formatBreakdownRow('Hidden Wild Canastas', a.hidden_wild_bonus, b.hidden_wild_bonus)
  if (hiddenRow) rows.push(hiddenRow)

  const goingOutRow = formatBreakdownRow('Going Out Bonus', a.going_out_bonus, b.going_out_bonus)
  if (goingOutRow) rows.push(goingOutRow)

  // Separator if we have penalties
  const hasPenalties = (a.hand_penalties || 0) !== 0 || (a.red_three_penalties || 0) !== 0 || (a.black_three_penalties || 0) !== 0 ||
                       (b.hand_penalties || 0) !== 0 || (b.red_three_penalties || 0) !== 0 || (b.black_three_penalties || 0) !== 0
  if (hasPenalties) {
    rows.push({ label: '', a: '', b: '', isSeparator: true })
  }

  // Penalties section (these are already negative from backend)
  const handRow = formatBreakdownRow('Hand Card Penalties', a.hand_penalties, b.hand_penalties)
  if (handRow) rows.push(handRow)

  const red3Row = formatBreakdownRow('Red 3 Penalties', a.red_three_penalties, b.red_three_penalties)
  if (red3Row) rows.push(red3Row)

  const black3Row = formatBreakdownRow('Black 3 Penalties', a.black_three_penalties, b.black_three_penalties)
  if (black3Row) rows.push(black3Row)

  // Total row
  rows.push({ label: '', a: '', b: '', isSeparator: true })
  rows.push({
    label: 'ROUND TOTAL',
    a: (a.total ?? 0) >= 0 ? `+${(a.total ?? 0).toLocaleString()}` : (a.total ?? 0).toLocaleString(),
    b: (b.total ?? 0) >= 0 ? `+${(b.total ?? 0).toLocaleString()}` : (b.total ?? 0).toLocaleString(),
    isTotal: true,
  })

  return rows
})

/**
 * Default score object
 */
function defaultScore(): TeamScore {
  return {
    canastas: { natural: 0, mixed: 0, wild: 0 },
    card_points: 0,
    going_out_bonus: 0,
    penalties: { red_3s: 0, hand_cards: 0 },
    round_total: 0,
  }
}

/**
 * Skip to summary phase
 */
function skipToSummary(): void {
  skipped.value = true
  waitingForContinue.value = false
  if (continueResolver) {
    continueResolver()
    continueResolver = null
  }
  currentPhase.value = 'summary'

  // Set final totals
  winnerCanastaTotal.value =
    winnerScore.value.canastas.natural * 500 +
    winnerScore.value.canastas.mixed * 300 +
    winnerScore.value.canastas.wild * 1000
  loserCanastaTotal.value =
    loserScore.value.canastas.natural * 500 +
    loserScore.value.canastas.mixed * 300 +
    loserScore.value.canastas.wild * 1000
}

/**
 * Check if team has any canastas to display
 */
function hasCanastas(score: typeof winnerScore.value): boolean {
  return (
    score.canastas.natural > 0 ||
    score.canastas.mixed > 0 ||
    score.canastas.wild > 0
  )
}


/**
 * Check if team has any card points to display
 */
function hasCardPoints(score: typeof winnerScore.value): boolean {
  return score.card_points > 0
}

/**
 * Check if team has any penalties to display
 */
function hasPenalties(score: typeof winnerScore.value): boolean {
  return score.penalties.red_3s > 0 || score.penalties.hand_cards > 0
}

/**
 * Advance to next winner phase (skipping empty phases)
 */
async function advanceWinnerPhase(fromPhase: 'canastas' | 'piles' | 'penalties'): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] advanceWinnerPhase from:', fromPhase)
  if (skipped.value) return

  if (fromPhase === 'canastas') {
    // After canastas, try piles
    if (hasCardPoints(winnerScore.value)) {
      Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing from canastas to winner-piles')
      currentPhase.value = 'winner-piles'
    } else if (hasPenalties(winnerScore.value)) {
      Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing from canastas to winner-penalties')
      currentPhase.value = 'winner-penalties'
    } else {
      // No piles or penalties - voice-only for no penalties
      await voiceOnlyPhase(NARRATION.penalties.none(winnerTeamName.value))
      if (skipped.value) return
      Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing from canastas to team-pause')
      currentPhase.value = 'team-pause'
      await startTeamPause()
    }
  } else if (fromPhase === 'piles') {
    // After piles, try penalties
    if (hasPenalties(winnerScore.value)) {
      Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing from piles to winner-penalties')
      currentPhase.value = 'winner-penalties'
    } else {
      // No penalties - voice-only announcement
      await voiceOnlyPhase(NARRATION.penalties.none(winnerTeamName.value))
      if (skipped.value) return
      Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing from piles to team-pause')
      currentPhase.value = 'team-pause'
      await startTeamPause()
    }
  } else if (fromPhase === 'penalties') {
    Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing from penalties to team-pause')
    currentPhase.value = 'team-pause'
    await startTeamPause()
  }
}

/**
 * Advance to next loser phase (skipping empty phases)
 */
async function advanceLoserPhase(fromPhase: 'canastas' | 'piles' | 'penalties'): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] advanceLoserPhase from:', fromPhase)
  if (skipped.value) return

  if (fromPhase === 'canastas') {
    if (hasCardPoints(loserScore.value)) {
      Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing loser from canastas to loser-piles')
      currentPhase.value = 'loser-piles'
    } else if (hasPenalties(loserScore.value)) {
      Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing loser from canastas to loser-penalties')
      currentPhase.value = 'loser-penalties'
    } else {
      // No piles or penalties - voice-only for no penalties
      await voiceOnlyPhase(NARRATION.penalties.none(loserTeamName.value))
      if (skipped.value) return
      Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing loser from canastas to summary')
      await showSummary()
    }
  } else if (fromPhase === 'piles') {
    if (hasPenalties(loserScore.value)) {
      Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing loser from piles to loser-penalties')
      currentPhase.value = 'loser-penalties'
    } else {
      // No penalties - voice-only announcement
      await voiceOnlyPhase(NARRATION.penalties.none(loserTeamName.value))
      if (skipped.value) return
      Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing loser from piles to summary')
      await showSummary()
    }
  } else if (fromPhase === 'penalties') {
    Logger.info(LogCategory.GAME, '[RoundEndModal] Advancing loser from penalties to summary')
    await showSummary()
  }
}

/**
 * Phase completion handlers - now with pause points for debugging
 */
async function onWinnerCanastaComplete(cleanTotal: number, dirtyWildTotal: number): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] onWinnerCanastaComplete:', { cleanTotal, dirtyWildTotal })
  winnerCanastaTotal.value = cleanTotal + dirtyWildTotal

  // Animate canasta counting with blips (like 100pt piles)
  const canastaCount = winnerScore.value.canastas.natural +
                       winnerScore.value.canastas.mixed +
                       winnerScore.value.canastas.wild
  if (canastaCount > 0 && !skipped.value) {
    // Build counting numbers: 5, 10, 15 for clean (500 each) and 3, 6, 9 for dirty (300 each)
    const cleanNumbers = Array.from(
      { length: winnerScore.value.canastas.natural },
      (_, i) => (i + 1) * 5  // 5, 10, 15... (represents 500s in hundreds)
    )
    const dirtyWildNumbers = Array.from(
      { length: winnerScore.value.canastas.mixed + winnerScore.value.canastas.wild },
      (_, i) => (i + 1) * 3  // 3, 6, 9... (represents 300s in hundreds)
    )
    const allNumbers = [...cleanNumbers, ...dirtyWildNumbers]

    // Estimate voice duration for synchronization
    const voiceDuration = estimateVoiceDuration(allNumbers)

    // Start voice counting and canasta highlighting in parallel
    await Promise.all([
      speakCounting(allNumbers, ''),
      winnerCanastaRef.value?.animateCanastaCount(voiceDuration)
    ])

    // After counting, announce the canasta base total
    if (!skipped.value) {
      const total = cleanTotal + dirtyWildTotal
      await speak(`${winnerTeamName.value}'s base is ${formatPointsForSpeech(total)} points.`, 'counter')
    }
  }

  // Wait for user to click Continue before advancing
  await waitForUserContinue()
  if (skipped.value) return

  advanceWinnerPhase('canastas')
}

/**
 * Handle deal-complete from winner CardPileCounter
 * Orchestrates voice counting with pile highlighting animation
 * Counts "1, 2, 3..." then announces total like "605 points will be added to your base"
 */
async function onWinnerDealComplete(total: number): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] onWinnerDealComplete:', total)
  if (skipped.value) return

  const pileCount = Math.floor(total / 100)
  if (pileCount > 0) {
    // Build counting numbers: 1, 2, 3... (not 100, 200, 300)
    const numbers = Array.from({ length: pileCount }, (_, i) => i + 1)

    // Estimate voice duration for synchronization
    const voiceDuration = estimateVoiceDuration(numbers)

    // Start voice counting and pile highlighting in parallel
    await Promise.all([
      speakCounting(numbers, ''),
      winnerPileCounterRef.value?.animatePileCounting(voiceDuration)
    ])

    // After counting, announce the total - ensure it completes
    if (!skipped.value) {
      await speak(`${formatPointsForSpeech(total)} points will be added to your base.`, 'counter')
    }
  } else {
    // No piles to count, but still need to trigger countComplete
    await winnerPileCounterRef.value?.animatePileCounting(0)
  }
}

async function onWinnerPileComplete(_total: number): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] onWinnerPileComplete:', _total)

  // Wait for user to click Continue before advancing
  await waitForUserContinue()
  if (skipped.value) return

  advanceWinnerPhase('piles')
}

async function onWinnerPenaltyComplete(_total: number): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] onWinnerPenaltyComplete:', _total)

  // Wait for user to click Continue before advancing
  await waitForUserContinue()
  if (skipped.value) return

  advanceWinnerPhase('penalties')
}

/**
 * Handle board highlight completion
 * Called by parent (Game.vue) when GameBoard finishes highlighting canastas
 *
 * @param team - The team that was just highlighted ('A' or 'B')
 * @param canastaTotal - Total canasta points for the highlighted team
 */
async function onBoardHighlightComplete(team: 'A' | 'B', canastaTotal: number): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] onBoardHighlightComplete:', { team, canastaTotal })

  if (team === winnerTeam.value) {
    // Winner team highlighting complete
    winnerCanastaTotal.value = canastaTotal
    // Advance to winner piles or next phase
    advanceWinnerPhase('canastas')
  } else {
    // Loser team highlighting complete
    loserCanastaTotal.value = canastaTotal
    // Advance to loser piles or next phase
    advanceLoserPhase('canastas')
  }
}

async function startTeamPause(): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] startTeamPause - loserScore:', loserScore.value)
  await teamPause()
  if (skipped.value) return

  // Start loser team - find first phase with content
  if (hasCanastas(loserScore.value)) {
    Logger.info(LogCategory.GAME, '[RoundEndModal] Going to board-canastas-loser (on-board highlighting)')
    currentPhase.value = 'board-canastas-loser'
    // Emit event to parent to trigger GameBoard highlighting
    // Modal will be hidden during this phase
    emit('request-board-highlight', loserTeam.value, loserScore.value.canastas, loserTeamName.value)
    // Parent will call onBoardHighlightComplete when done
  } else {
    // No canastas - voice-only announcement
    await voiceOnlyPhase(NARRATION.canastas.empty(loserTeamName.value))
    if (skipped.value) return

    if (hasCardPoints(loserScore.value)) {
      Logger.info(LogCategory.GAME, '[RoundEndModal] Going to loser-piles')
      currentPhase.value = 'loser-piles'
    } else if (hasPenalties(loserScore.value)) {
      Logger.info(LogCategory.GAME, '[RoundEndModal] Going to loser-penalties')
      currentPhase.value = 'loser-penalties'
    } else {
      // Loser has nothing to show - voice-only for no penalties
      await voiceOnlyPhase(NARRATION.penalties.none(loserTeamName.value))
      if (skipped.value) return
      Logger.info(LogCategory.GAME, '[RoundEndModal] Loser has nothing, going to summary')
      await showSummary()
    }
  }
}

async function onLoserCanastaComplete(cleanTotal: number, dirtyWildTotal: number): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] onLoserCanastaComplete:', { cleanTotal, dirtyWildTotal })
  loserCanastaTotal.value = cleanTotal + dirtyWildTotal

  // Animate canasta counting with blips (like 100pt piles)
  const canastaCount = loserScore.value.canastas.natural +
                       loserScore.value.canastas.mixed +
                       loserScore.value.canastas.wild
  if (canastaCount > 0 && !skipped.value) {
    // Build counting numbers: 5, 10, 15 for clean (500 each) and 3, 6, 9 for dirty (300 each)
    const cleanNumbers = Array.from(
      { length: loserScore.value.canastas.natural },
      (_, i) => (i + 1) * 5  // 5, 10, 15... (represents 500s in hundreds)
    )
    const dirtyWildNumbers = Array.from(
      { length: loserScore.value.canastas.mixed + loserScore.value.canastas.wild },
      (_, i) => (i + 1) * 3  // 3, 6, 9... (represents 300s in hundreds)
    )
    const allNumbers = [...cleanNumbers, ...dirtyWildNumbers]

    // Estimate voice duration for synchronization
    const voiceDuration = estimateVoiceDuration(allNumbers)

    // Start voice counting and canasta highlighting in parallel
    await Promise.all([
      speakCounting(allNumbers, ''),
      loserCanastaRef.value?.animateCanastaCount(voiceDuration)
    ])

    // After counting, announce the canasta base total
    if (!skipped.value) {
      const total = cleanTotal + dirtyWildTotal
      await speak(`${loserTeamName.value}'s base is ${formatPointsForSpeech(total)} points.`, 'counter')
    }
  }

  // Wait for user to click Continue before advancing
  await waitForUserContinue()
  if (skipped.value) return

  advanceLoserPhase('canastas')
}

/**
 * Handle deal-complete from loser CardPileCounter
 * Orchestrates voice counting with pile highlighting animation
 * Counts "1, 2, 3..." then announces total like "605 points will be added to your base"
 */
async function onLoserDealComplete(total: number): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] onLoserDealComplete:', total)
  if (skipped.value) return

  const pileCount = Math.floor(total / 100)
  if (pileCount > 0) {
    // Build counting numbers: 1, 2, 3... (not 100, 200, 300)
    const numbers = Array.from({ length: pileCount }, (_, i) => i + 1)

    // Estimate voice duration for synchronization
    const voiceDuration = estimateVoiceDuration(numbers)

    // Start voice counting and pile highlighting in parallel
    await Promise.all([
      speakCounting(numbers, ''),
      loserPileCounterRef.value?.animatePileCounting(voiceDuration)
    ])

    // After counting, announce the total - ensure it completes
    if (!skipped.value) {
      await speak(`${formatPointsForSpeech(total)} points will be added to your base.`, 'counter')
    }
  } else {
    // No piles to count, but still need to trigger countComplete
    await loserPileCounterRef.value?.animatePileCounting(0)
  }
}

async function onLoserPileComplete(_total: number): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] onLoserPileComplete:', _total)

  // Wait for user to click Continue before advancing
  await waitForUserContinue()
  if (skipped.value) return

  advanceLoserPhase('piles')
}

async function onLoserPenaltyComplete(_total: number): Promise<void> {
  Logger.debug(LogCategory.GAME, '[RoundEndModal] onLoserPenaltyComplete:', _total)

  // Wait for user to click Continue before advancing
  await waitForUserContinue()
  if (skipped.value) return

  advanceLoserPhase('penalties')
}

async function showSummary(): Promise<void> {
  currentPhase.value = 'summary'
  playFinalTotal()

  // Announce winner
  const winnerPoints = winnerScore.value.round_total
  await speak(
    `${winnerTeamName.value} wins the round with ${formatPointsForSpeech(winnerPoints)} points!`,
    'announcer'
  )
}

/**
 * Start the animation sequence
 */
async function startSequence(): Promise<void> {
  if (!props.isOpen || !props.data) return

  Logger.info(LogCategory.GAME, '[RoundEndModal] startSequence - props.data:', props.data)
  Logger.info(LogCategory.GAME, '[RoundEndModal] winnerScore:', winnerScore.value)
  Logger.info(LogCategory.GAME, '[RoundEndModal] loserScore:', loserScore.value)
  Logger.debug(LogCategory.GAME, '[RoundEndModal] hasCanastas(winner):', hasCanastas(winnerScore.value))
  Logger.debug(LogCategory.GAME, '[RoundEndModal] hasCardPoints(winner):', hasCardPoints(winnerScore.value))
  Logger.debug(LogCategory.GAME, '[RoundEndModal] hasPenalties(winner):', hasPenalties(winnerScore.value))

  skipped.value = false
  winnerCanastaTotal.value = 0
  loserCanastaTotal.value = 0

  // Start video backdrop celebration (plays behind modal content)
  startVideoBackdrop()

  // Phase 1: Announcement (brisk 1.5s)
  currentPhase.value = 'announcement'
  playGoOut()
  await speak(`${wentOutPlayer.value} goes out!`, 'dealer')
  await delay(1500)

  if (skipped.value) return

  // Find first winner phase with content
  if (hasCanastas(winnerScore.value)) {
    Logger.info(LogCategory.GAME, '[RoundEndModal] Going to board-canastas-winner (on-board highlighting)')
    currentPhase.value = 'board-canastas-winner'
    // Emit event to parent to trigger GameBoard highlighting
    // Modal will be hidden during this phase
    emit('request-board-highlight', winnerTeam.value, winnerScore.value.canastas, winnerTeamName.value)
    // Parent will call onBoardHighlightComplete when done
  } else {
    // No canastas - voice-only announcement
    await voiceOnlyPhase(NARRATION.canastas.empty(winnerTeamName.value))
    if (skipped.value) return

    if (hasCardPoints(winnerScore.value)) {
      Logger.info(LogCategory.GAME, '[RoundEndModal] Going to winner-piles')
      currentPhase.value = 'winner-piles'
    } else if (hasPenalties(winnerScore.value)) {
      Logger.info(LogCategory.GAME, '[RoundEndModal] Going to winner-penalties')
      currentPhase.value = 'winner-penalties'
    } else {
      // Winner has nothing to show, go to team pause for loser
      Logger.info(LogCategory.GAME, '[RoundEndModal] Winner has nothing, going to team-pause')
      currentPhase.value = 'team-pause'
      await startTeamPause()
    }
  }

  // The rest is driven by event callbacks
}

/**
 * Handle continue button
 */
function handleContinue(): void {
  console.log('[RoundEndModal] handleContinue called, emitting continue and close')
  playClick()
  emit('continue')
  emit('close')
}

/**
 * Handle modal close
 */
function handleClose(): void {
  if (currentPhase.value === 'summary') {
    emit('close')
  }
}

/**
 * Watch for modal open
 */
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      startSequence()
    } else {
      // Reset video state when modal closes
      currentPhase.value = 'idle'
      showVideoBackdrop.value = false
      celebrationVideo.value = null
      videoError.value = false
      if (videoRef.value) {
        videoRef.value.pause()
        videoRef.value.currentTime = 0
      }
    }
  }
)

/**
 * Start on mount if already open
 */
onMounted(() => {
  if (props.isOpen) {
    startSequence()
  }
})

/**
 * Cleanup on unmount
 */
onUnmounted(() => {
  victoryVideo.cleanup()
})

// Expose method for parent component (Game.vue) to call when board highlighting completes
defineExpose({
  onBoardHighlightComplete,
})
</script>

<style scoped>
/* Import premium typography */
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Outfit:wght@400;500;600;700&display=swap');

.round-end-container {
  position: relative;
  padding: 1rem;
  padding-bottom: 5rem; /* Space for fixed continue button */
  min-height: 400px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background: linear-gradient(135deg, #0f1419 0%, #1a2634 50%, #0d1117 100%);
  border-radius: 16px;
  overflow-y: auto;
}

/* Subtle noise texture overlay */
.round-end-container::before {
  content: '';
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
  pointer-events: none;
  z-index: 0;
}

/* Gold accent gradient at top */
.round-end-container::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: radial-gradient(ellipse at top center, rgba(232, 197, 71, 0.1) 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}

/* Ensure content is above overlays */
.round-end-container > * {
  position: relative;
  z-index: 1;
}

/* Video celebration backdrop */
.round-end-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  border-radius: 16px;
  opacity: 0.4;
  filter: brightness(0.7) saturate(1.2);
}

/* Video fade transition */
.video-fade-enter-active {
  transition: opacity 0.5s ease;
}

.video-fade-leave-active {
  transition: opacity 1s ease;
}

.video-fade-enter-from,
.video-fade-leave-to {
  opacity: 0;
}

/* Phase 1: Went Out Announcement */
.went-out-announcement {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  text-align: center;
}

.celebration-icon {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 1.5s ease-in-out infinite;
}

.celebration-icon .material-symbols-outlined {
  font-size: 4rem;
  color: #fbbf24;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

.went-out-title {
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  letter-spacing: 0.02em;
  animation: goOutExplosion 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes goOutExplosion {
  0% {
    transform: scale(0) rotate(-5deg);
    opacity: 0;
  }
  60% {
    transform: scale(1.15) rotate(2deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.team-badge {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1.5rem;
  font-weight: 600;
}

.team-badge.team-a {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.2));
  border: 2px solid #60a5fa;
  color: #93c5fd;
}

.team-badge.team-b {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2));
  border: 2px solid #f87171;
  color: #fca5a5;
}

.team-badge .material-symbols-outlined {
  font-size: 2rem;
}

/* Team Scoring Phases - Glass morphism */
.team-scoring {
  width: 100%;
  max-width: 700px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.phase-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 0.75rem;
  text-align: center;
  letter-spacing: 0.02em;
}

/* Team Pause */
.team-pause {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
}

.pause-indicator {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
}

.pause-indicator .material-symbols-outlined {
  font-size: 2rem;
  color: #60a5fa;
}

/* Round Summary */
.round-summary {
  width: 100%;
  max-width: 750px;
}

.summary-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 2rem;
  text-align: center;
  letter-spacing: 0.02em;
}

/* Side-by-side comparison layout */
.teams-comparison {
  display: flex;
  align-items: stretch;
  gap: 1rem;
  margin-bottom: 2rem;
}

.team-card {
  flex: 1;
  position: relative;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  text-align: center;
  transition: all 0.5s ease-out;
}

.team-card.team-a {
  border-color: rgba(96, 165, 250, 0.4);
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(37, 99, 235, 0.04));
}

.team-card.team-b {
  border-color: rgba(248, 113, 113, 0.4);
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(220, 38, 38, 0.04));
}

/* Winner glow effect */
.team-card.is-winner {
  border-color: #e8c547;
  box-shadow:
    0 0 30px rgba(232, 197, 71, 0.3),
    0 0 60px rgba(232, 197, 71, 0.15);
  animation: winnerGlow 2s ease-in-out infinite;
}

@keyframes winnerGlow {
  0%,
  100% {
    box-shadow:
      0 0 30px rgba(232, 197, 71, 0.3),
      0 0 60px rgba(232, 197, 71, 0.15);
  }
  50% {
    box-shadow:
      0 0 40px rgba(232, 197, 71, 0.5),
      0 0 80px rgba(232, 197, 71, 0.25);
  }
}

/* Winner crown */
.winner-crown {
  position: absolute;
  top: -24px;
  left: 50%;
  transform: translateX(-50%);
  color: #e8c547;
  animation: crownBounce 1s ease-out;
}

.winner-crown .material-symbols-outlined {
  font-size: 2.5rem;
  filter: drop-shadow(0 2px 4px rgba(232, 197, 71, 0.5));
}

@keyframes crownBounce {
  0% {
    transform: translateX(-50%) translateY(-30px) scale(0);
  }
  60% {
    transform: translateX(-50%) translateY(5px) scale(1.2);
  }
  100% {
    transform: translateX(-50%) translateY(0) scale(1);
  }
}

.team-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.team-card.team-a .team-header {
  color: #93c5fd;
}

.team-card.team-b .team-header {
  color: #fca5a5;
}

.team-header .team-name {
  font-family: 'Outfit', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
}

.round-points-display {
  margin-bottom: 1rem;
}

.round-points-display .label {
  display: block;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.round-points-display .value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #10b981;
}

.total-score-display .value {
  font-family: 'Outfit', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #e8c547;
  line-height: 1;
}

.total-score-display .label {
  display: block;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0.25rem;
}

/* VS Divider */
.vs-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.25);
  padding: 0 0.5rem;
  align-self: center;
}

.btn-continue {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  border: none;
  border-radius: 12px;
  color: #0d1f2d;
  font-size: 1.125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
}

.btn-continue:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-continue:active {
  transform: translateY(0);
}

.btn-continue .material-symbols-outlined {
  font-size: 1.5rem;
}

/* Debug Continue Button (pause points) - Fixed at bottom to avoid overlap */
.continue-prompt {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10001;
  animation: continueSlideUp 0.3s ease-out;
}

@keyframes continueSlideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.btn-continue-debug {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  border: none;
  border-radius: 12px;
  color: #0d1117;
  font-family: 'Outfit', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow:
    0 4px 20px rgba(232, 197, 71, 0.4),
    0 0 40px rgba(232, 197, 71, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.btn-continue-debug:hover {
  transform: scale(1.05);
  box-shadow:
    0 6px 30px rgba(232, 197, 71, 0.5),
    0 0 60px rgba(232, 197, 71, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.btn-continue-debug:active {
  transform: scale(0.98);
}

.btn-continue-debug .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Score Breakdown Accordion */
.breakdown-accordion {
  width: 100%;
  margin-bottom: 1.5rem;
}

.breakdown-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Outfit', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.breakdown-toggle:hover {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border-color: rgba(255, 255, 255, 0.2);
}

.breakdown-toggle .toggle-icon {
  transition: transform 0.3s ease;
}

.breakdown-toggle .toggle-icon.rotated {
  transform: rotate(180deg);
}

.breakdown-content {
  margin-top: 0.75rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  overflow: hidden;
}

.breakdown-header,
.breakdown-row {
  display: grid;
  grid-template-columns: 1fr 100px 100px;
  gap: 0.5rem;
  padding: 0.5rem 0;
  align-items: center;
}

.breakdown-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  margin-bottom: 0.5rem;
  padding-bottom: 0.75rem;
}

.breakdown-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: left;
}

.breakdown-value {
  font-family: 'Outfit', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: right;
  color: rgba(255, 255, 255, 0.9);
}

.breakdown-value.positive {
  color: #10b981;
}

.breakdown-value.negative {
  color: #f87171;
}

.team-a-header {
  color: #93c5fd;
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.team-b-header {
  color: #fca5a5;
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.breakdown-row.is-separator {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  padding: 0;
  margin: 0.5rem 0;
  grid-template-columns: 1fr;
}

.breakdown-row.is-total {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 0.5rem;
  padding-top: 0.75rem;
}

.breakdown-row.is-total .breakdown-label {
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.9rem;
}

.breakdown-row.is-total .breakdown-value {
  font-weight: 700;
  font-size: 1rem;
  color: #e8c547;
}

/* Slide transition for accordion */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .teams-comparison {
    flex-direction: column;
    gap: 1.5rem;
  }

  .vs-divider {
    padding: 0.5rem 0;
  }

  .went-out-title {
    font-size: 1.75rem;
  }

  .celebration-icon {
    width: 80px;
    height: 80px;
  }

  .celebration-icon .material-symbols-outlined {
    font-size: 3rem;
  }

  .total-score-display .value {
    font-size: 2rem;
  }

  .winner-crown {
    top: -20px;
  }

  .winner-crown .material-symbols-outlined {
    font-size: 2rem;
  }

  /* Breakdown accordion responsive */
  .breakdown-header,
  .breakdown-row {
    grid-template-columns: 1fr 80px 80px;
    gap: 0.25rem;
  }

  .breakdown-label {
    font-size: 0.75rem;
  }

  .breakdown-value {
    font-size: 0.8rem;
  }

  .team-a-header,
  .team-b-header {
    font-size: 0.65rem;
  }
}
</style>
